import { WalletReportsCatalog } from 'src/app/models/WalletReports';

/**
 * Catálogo padrão (M7, task 17tm466nz8b) — espelha byte a byte o exemplo publicado pelo
 * m7-back em `~/trabalhos/m7-coord.md` (seção 4, `GET /wallet/reports/catalog`). Serve de
 * fixture para o mock e de fallback se a chamada real falhar antes do configurador abrir.
 *
 * Sem o tipo `responses` ("Respostas recebidas"): cancelado pelo Misael (14/10). Só 2
 * formatos (`csv_br`, `xlsx`): sem "CSV internacional".
 */
export const REPORTS_CATALOG: WalletReportsCatalog = {
  limits: { immediate_rows: 10000, max_rows: 1000000, max_period_days: 366, ttl_days: 7, preview_rows: 50 },
  formats: [
    { key: 'csv_br', label: 'CSV (Excel Brasil)' },
    { key: 'xlsx', label: 'Excel (.xlsx)' },
  ],
  types: [
    {
      key: 'dispatches',
      label: 'Disparos',
      filters: {
        channels: ['sms', 'whatsapp', 'call'],
        origins: ['automation', 'broadcast', 'conversation', 'manual_resend', 'partner_api'],
        statuses: ['delivered', 'sent', 'failed', 'read', 'answered', 'no_answer', 'voicemail'],
      },
      columns: [
        { key: 'charged_at', label: 'Data da cobrança', type: 'datetime', default: true },
        { key: 'sent_at', label: 'Data do envio', type: 'datetime', default: false },
        { key: 'channel', label: 'Canal', type: 'text', default: true },
        { key: 'origin', label: 'Origem', type: 'text', default: true },
        { key: 'origin_name', label: 'Automação/Disparo', type: 'text', default: true },
        { key: 'step', label: 'Passo', type: 'integer', default: false },
        { key: 'template', label: 'Template', type: 'text', default: false },
        { key: 'message', label: 'Mensagem', type: 'text', default: true },
        { key: 'phone', label: 'Telefone', type: 'phone', default: true },
        { key: 'lead_name', label: 'Nome do lead', type: 'text', default: true },
        { key: 'delivery_status', label: 'Status', type: 'text', default: true },
        { key: 'failure_reason', label: 'Motivo da falha', type: 'text', default: false },
        { key: 'call_duration', label: 'Duração (s)', type: 'integer', default: false },
        { key: 'quantity', label: 'Qtd.', type: 'integer', default: false },
        { key: 'entry_id', label: 'Lançamento', type: 'text', default: false },
        { key: 'batch_size', label: 'Mensagens no lote', type: 'integer', default: false },
        { key: 'unit_cost', label: 'Custo unitário (R$)', type: 'money', default: false },
        { key: 'amount', label: 'Valor rateado (R$)', type: 'money', default: true },
        { key: 'entry_amount', label: 'Valor do lançamento (R$)', type: 'money', default: false },
      ],
    },
    {
      key: 'entries',
      label: 'Lançamentos da carteira',
      filters: { entry_types: ['debit', 'credit', 'bonus', 'refund', 'adjustment', 'migration'], services: ['sms', 'whatsapp', 'call', 'ai'] },
      columns: [
        { key: 'created_at', label: 'Data', type: 'datetime', default: true },
        { key: 'entry_type', label: 'Tipo', type: 'text', default: true },
        { key: 'service', label: 'Serviço', type: 'text', default: true },
        { key: 'quantity', label: 'Qtd.', type: 'integer', default: false },
        { key: 'unit', label: 'Unidade', type: 'text', default: false },
        { key: 'observation', label: 'Observação', type: 'text', default: true },
        { key: 'invoice_url', label: 'Nota Fiscal', type: 'text', default: false },
        { key: 'entry_id', label: 'ID', type: 'text', default: false },
        { key: 'amount', label: 'Valor (R$)', type: 'money', default: true },
        { key: 'balance_after', label: 'Saldo após (R$)', type: 'money', default: false },
      ],
    },
    {
      key: 'recharges',
      label: 'Recargas e notas fiscais',
      filters: {
        payment_statuses: ['paid', 'pending', 'cancelled'],
        kinds: ['value', 'legacy_package', 'subscription', 'promotion_subscription', 'referral_bonus', 'transfer', 'domain_addon', 'other'],
        invoice_statuses: ['issued', 'pending', 'processing', 'failed', 'not_applicable'],
      },
      columns: [
        { key: 'created_at', label: 'Data', type: 'datetime', default: true },
        { key: 'recharge_id', label: 'Nº da recarga', type: 'text', default: true },
        { key: 'kind', label: 'Tipo', type: 'text', default: true },
        { key: 'payment_type', label: 'Forma de pagamento', type: 'text', default: true },
        { key: 'payment_status', label: 'Status', type: 'text', default: true },
        { key: 'confirmed_at', label: 'Pago em', type: 'datetime', default: false },
        { key: 'legacy_credits', label: 'Créditos do pacote', type: 'integer', default: false },
        { key: 'invoice_status', label: 'NF', type: 'text', default: true },
        { key: 'invoice_url', label: 'Link da NF', type: 'text', default: false },
        { key: 'paid_amount', label: 'Valor pago (R$)', type: 'money', default: true },
        { key: 'credited_amount', label: 'Creditado na carteira (R$)', type: 'money', default: true },
        { key: 'bonus_amount', label: 'Bônus (R$)', type: 'money', default: false },
      ],
    },
    {
      key: 'consumption',
      label: 'Consumo por período',
      filters: { group_by: ['day', 'week', 'month'], channels: ['sms', 'whatsapp', 'call', 'ai'], split_by_origin: 'bool' },
      columns: [
        { key: 'period', label: 'Período', type: 'text', default: true },
        { key: 'channel', label: 'Canal', type: 'text', default: true },
        { key: 'origin', label: 'Origem', type: 'text', default: false },
        { key: 'entries', label: 'Lançamentos', type: 'integer', default: false },
        { key: 'units', label: 'Quantidade', type: 'integer', default: true },
        { key: 'unit', label: 'Unidade', type: 'text', default: false },
        { key: 'amount', label: 'Total (R$)', type: 'money', default: true },
      ],
    },
  ],
};

/** `dispatches` → "disparos" etc. — prefixo do nome do arquivo (`reportFileName`). */
export const REPORT_TYPE_FILE_PREFIX: { [key: string]: string } = {
  dispatches: 'disparos',
  entries: 'lancamentos',
  recharges: 'recargas',
  consumption: 'consumo',
};
